import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import CloseIcon from 'components/material-symbol/icons/Close'
import FileUploadOutlinedIcon from 'components/material-symbol/icons/FileUpload'
import LinkIcon from 'components/material-symbol/icons/Link'
import SendIcon from 'components/material-symbol/icons/Send'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import VideoFilePicker from 'components/video-file-picker'
import {
  getVideoFileError,
  MAX_VIDEO_SIZE_MB,
  uploadVideo,
} from 'lib/cloudinary/upload-video'
import { useSubmitVirtualCoachingResponse } from './api-hooks'
import OfferingTypeCardGroup from 'pages/pga-coach/bookings/booking-types/components/offering-type-card-group'
import { SettingsSection } from 'pages/pga-coach/settings/settings-components'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import { useStyles } from './styles'
import {
  trackVirtualCoachingEvent,
  trackVirtualCoachingFormSubmit,
  VIRTUAL_COACHING_EVENTS,
} from './analytics'
import { formatFileSize, getStudentName, isOnformUrl } from './utils'

const REPLY_MODE = {
  ONFORM: 'onform',
  UPLOAD: 'upload',
}

// Same either/or option cards the offering form uses for its offering type.
const REPLY_OPTIONS = [
  {
    value: REPLY_MODE.ONFORM,
    title: 'Onform link',
    description: 'Paste the share link from your Onform analysis.',
    icon: LinkIcon,
  },
  {
    value: REPLY_MODE.UPLOAD,
    title: 'Upload a video',
    description: 'Upload an MP4, MOV, or WebM analysis video (up to 100MB).',
    icon: FileUploadOutlinedIcon,
  },
]

const UPLOAD_ERROR_MESSAGE =
  'Upload failed. Check your connection and try again.'

// The selected video, in the golfer upload form's file row. The reply takes one
// video, so this is that list with a single item; `ReplyForm` renders one row
// per selected file when more are allowed.
const SelectedVideoRow = ({ file, progress, error, disabled, onRemove }) => {
  const classes = useStyles()
  const isUploading = progress !== null

  return (
    <li className={classes.fileItem}>
      <Box className={classes.iconTile} aria-hidden="true">
        <VideocamIcon />
      </Box>
      <Box className={classes.fileMeta}>
        <Typography className={classes.fileName} title={file.name}>
          {file.name}
        </Typography>
        {isUploading ? (
          <>
            <Typography className={classes.fileStatus}>
              Uploading… {progress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              className={classes.progress}
              aria-label={`Upload progress for ${file.name}`}
            />
          </>
        ) : error ? (
          <Typography
            className={`${classes.fileStatus} ${classes.fileStatusError}`}
            role="alert"
          >
            {error}
          </Typography>
        ) : (
          <Typography className={classes.fileStatus}>
            Ready to send · {formatFileSize(file.size)}
          </Typography>
        )}
      </Box>
      <Box className={classes.fileActions}>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onRemove}
          aria-label={`Remove ${file.name}`}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </li>
  )
}

SelectedVideoRow.propTypes = {
  file: PropTypes.object.isRequired,
  progress: PropTypes.number,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
}

const ReplyForm = ({ request }) => {
  const classes = useStyles()
  const settingsClasses = useSettingsStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [mode, setMode] = useState(REPLY_MODE.ONFORM)
  const [onformUrl, setOnformUrl] = useState('')
  const [file, setFile] = useState(null)
  const [uploadedUrl, setUploadedUrl] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [notes, setNotes] = useState('')
  const [fieldError, setFieldError] = useState(null)

  const [
    submitResponse,
    { loading: isSubmitting },
  ] = useSubmitVirtualCoachingResponse({
    refetchQueries: ['VirtualCoachingRequests', 'VirtualCoachingSummary'],
  })

  const isBusy = isSubmitting || uploadProgress !== null
  const trimmedOnformUrl = onformUrl.trim()
  const trimmedNotes = notes.trim()

  const handleModeChange = event => {
    setMode(event.target.value)
    setFieldError(null)
  }

  const selectFile = selected => {
    setFile(selected)
    setUploadedUrl(null)
    setUploadError(null)
    setFieldError(null)
  }

  // From the file picker button: the valid file, or the first rejection.
  const handleSelect = ({ files, errors }) => {
    if (errors.length > 0) {
      setFieldError(errors[0].message)
      return
    }
    if (files[0]) selectFile(files[0])
  }

  // Dropped on the zone. The API takes one reply video, so one file only.
  const handleDrop = event => {
    event.preventDefault()
    setDragging(false)
    if (isBusy) return
    const dropped = Array.from(event.dataTransfer?.files || [])
    if (dropped.length === 0) return
    if (dropped.length > 1) {
      setFieldError('Add one video at a time.')
      return
    }
    const error = getVideoFileError(dropped[0])
    if (error) {
      setFieldError(error)
      return
    }
    selectFile(dropped[0])
  }

  const handleDragOver = event => {
    event.preventDefault()
    if (!isBusy) setDragging(true)
  }

  // The button is only enabled once there is a video or an Onform link, so this
  // only checks that the link is an Onform share link.
  const validate = () =>
    mode === REPLY_MODE.ONFORM && !isOnformUrl(trimmedOnformUrl)
      ? 'Paste the share link from Onform (link.getonform.com).'
      : null

  const hasResponse =
    mode === REPLY_MODE.ONFORM ? Boolean(trimmedOnformUrl) : Boolean(file)

  const handleSubmit = async event => {
    event.preventDefault()
    const validationError = validate()
    setFieldError(validationError)
    if (validationError) return

    const input = {
      requestId: request.id,
      notes: trimmedNotes || null,
    }

    try {
      setUploadError(null)
      if (mode === REPLY_MODE.UPLOAD && file) {
        let url = uploadedUrl
        if (!url) {
          setUploadProgress(0)
          try {
            ;({ url } = await uploadVideo(file, {
              folder: 'virtual-coaching',
              onProgress: setUploadProgress,
            }))
          } catch (error) {
            setUploadError(UPLOAD_ERROR_MESSAGE)
            throw error
          }
          setUploadedUrl(url)
        }
        input.videoUrl = url
      } else if (mode === REPLY_MODE.ONFORM && trimmedOnformUrl) {
        input.onformUrl = trimmedOnformUrl
      }

      const { data } = await submitResponse({ variables: { input } })
      const result = data?.submitVirtualCoachingResponse

      if (!result?.success) {
        enqueueSnackbar(
          result?.message || 'Unable to share your analysis. Please try again.',
          { variant: 'error' },
        )
        return
      }

      enqueueSnackbar('Your analysis was shared with the student.', {
        variant: 'success',
      })
      trackVirtualCoachingFormSubmit('share-virtual-coaching-response')
      trackVirtualCoachingEvent(VIRTUAL_COACHING_EVENTS.REPLY_SHARED, {
        requestId: request.id,
        // Share is only enabled with an Onform link or a video.
        method: input.onformUrl ? 'onform' : 'upload',
        submittedAt: request.submittedAt,
      })
    } catch (error) {
      enqueueSnackbar('Unable to share your analysis. Please try again.', {
        variant: 'error',
      })
    } finally {
      setUploadProgress(null)
    }
  }

  const firstName =
    request.student?.firstName || getStudentName(request.student)

  return (
    <SettingsSection
      eyebrow="Send your analysis"
      subtitle={`Share an Onform link or upload a video. ${firstName} is notified as soon as you send it.`}
      classes={settingsClasses}
    >
      <Box
        className={classes.sectionBody}
        component="form"
        onSubmit={handleSubmit}
      >
        <Box className={classes.formField}>
          <OfferingTypeCardGroup
            options={REPLY_OPTIONS}
            value={mode}
            onChange={handleModeChange}
            readOnly={isBusy}
            ariaLabel="How are you sending your analysis?"
            idPrefix="virtual-coaching-reply"
          />
        </Box>

        {mode === REPLY_MODE.ONFORM ? (
          <TextField
            id="virtual-coaching-onform-url"
            className={classes.formField}
            inputProps={{ 'aria-label': 'Onform share link' }}
            placeholder="Paste your Onform share link"
            value={onformUrl}
            onChange={event => setOnformUrl(event.target.value)}
            disabled={isBusy}
            variant="outlined"
            size="small"
            fullWidth
            helperText="In Onform, share your analysis, copy the link, and paste it here."
          />
        ) : (
          <Box
            className={`${classes.formField} ${classes.dropZone} ${
              dragging ? classes.dropZoneActive : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={event => {
              // Moving onto the hint or button inside the box also fires this.
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setDragging(false)
              }
            }}
            onDrop={handleDrop}
            data-testid="virtual-coaching-reply-dropzone"
          >
            <Typography
              id="virtual-coaching-reply-hint"
              variant="body2"
              className={classes.helper}
            >
              {`Drag your analysis video here, or choose it from your device. MP4, MOV, or WebM, up to ${MAX_VIDEO_SIZE_MB}MB.`}
            </Typography>
            <VideoFilePicker
              onSelect={handleSelect}
              disabled={isBusy}
              label={file ? 'Choose a different video' : 'Choose video'}
              startIcon={<FileUploadOutlinedIcon />}
              inputTestId="virtual-coaching-reply-file"
              describedBy="virtual-coaching-reply-hint"
              variant="contained"
            />
          </Box>
        )}
        {fieldError ? (
          <Typography
            color="error"
            variant="body2"
            role="alert"
            className={classes.pickerError}
          >
            {fieldError}
          </Typography>
        ) : null}
        {mode === REPLY_MODE.UPLOAD && file ? (
          <ul className={classes.fileList} aria-label="Selected video">
            <SelectedVideoRow
              file={file}
              progress={uploadProgress}
              error={uploadError}
              disabled={isBusy}
              onRemove={() => {
                setFile(null)
                setUploadedUrl(null)
                setUploadError(null)
              }}
            />
          </ul>
        ) : null}

        <TextField
          id="virtual-coaching-notes"
          className={classes.formField}
          inputProps={{ 'aria-label': `Notes for ${firstName} (optional)` }}
          placeholder={`Notes for ${firstName} (optional)`}
          value={notes}
          onChange={event => setNotes(event.target.value)}
          disabled={isBusy}
          variant="outlined"
          multiline
          rows={4}
          fullWidth
          helperText="Keep it simple — the one thing that will help most, and a clear next step."
        />

        <Box className={classes.formFooter}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SendIcon aria-hidden />}
            classes={{ label: classes.buttonLabel }}
            disabled={isBusy || !hasResponse}
          >
            Share with {firstName}
          </Button>
        </Box>
      </Box>
    </SettingsSection>
  )
}

ReplyForm.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    submittedAt: PropTypes.string,
    student: PropTypes.object,
  }).isRequired,
}

export default ReplyForm
